import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCaseTasks } from "@/lib/CaseTasksContext";
import { useTeam } from "@/lib/useTeam";
import { caseTaskLabel, type CaseTask } from "@/lib/caseTasks";
import { firstNameOf } from "@/lib/kanbanTasks";
import { caseSummaryClient, caseSummaryTitle, useCaseSummaries, type CaseSummary } from "@/lib/useCaseSummaries";
import { TaskFlame } from "./TaskFlame";
import "./tasks.css";

export const NEW_TASK_TOAST_DURATION_MS = 10_000;
const MAX_VISIBLE = 4;

/**
 * Pop-up « nouvelle tâche » (toutes les pages protégées) : un par tâche reçue EN DIRECT
 * (`newTasksForMe` ne contient jamais les tâches du chargement de la page).
 */
export function NewTaskToast() {
  const { newTasksForMe, dismissNewTask } = useCaseTasks();
  const { profilesById } = useTeam();
  const visible = newTasksForMe.slice(-MAX_VISIBLE);
  const hidden = newTasksForMe.length - visible.length;
  const summaries = useCaseSummaries(visible.map((task) => task.case_id));

  if (newTasksForMe.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-3 z-[10000] flex flex-col items-center gap-2 px-4"
      role="region"
      aria-label="Nouvelles tâches"
    >
      {visible.map((task) => (
        <TaskToastItem
          key={task.id}
          task={task}
          summary={summaries.get(task.case_id)}
          author={firstNameOf(profilesById.get(task.created_by), "Le manager")}
          onDismiss={() => dismissNewTask(task.id)}
        />
      ))}
      {hidden > 0 && (
        <p className="pointer-events-auto rounded-full bg-gray-900/80 px-3 py-1 text-xs font-medium text-white shadow">
          + {hidden} autre{hidden > 1 ? "s" : ""} tâche{hidden > 1 ? "s" : ""} dans « À faire »
        </p>
      )}
    </div>
  );
}

interface TaskToastItemProps {
  task: CaseTask;
  summary: CaseSummary | undefined;
  author: string;
  onDismiss: () => void;
}

function TaskToastItem({ task, summary, author, onDismiss }: TaskToastItemProps) {
  const navigate = useNavigate();
  const [paused, setPaused] = useState(false);
  const remaining = useRef(NEW_TASK_TOAST_DURATION_MS);
  const startedAt = useRef(Date.now());

  // Minuteur qui s'arrête au survol / focus et reprend là où il en était.
  useEffect(() => {
    if (paused) return;
    startedAt.current = Date.now();
    const timer = window.setTimeout(onDismiss, remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    };
  }, [paused, onDismiss]);

  const title = caseSummaryTitle(summary);
  const client = caseSummaryClient(summary);

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false);
      }}
      className={`task-toast-in pointer-events-auto relative w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/10 ${paused ? "task-toast-paused" : ""}`}
    >
      <div className="flex items-start gap-3 p-3.5 pr-10">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-orange-50 ring-1 ring-orange-100">
          <TaskFlame className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-700">Nouvelle tâche</p>
          <p className="text-sm font-semibold text-gray-900">{caseTaskLabel(task.task_type)}</p>
          <p className="truncate text-sm text-gray-700">
            {title}
            {client && client !== title && <span className="text-gray-500"> · {client}</span>}
            {summary?.center && <span className="text-gray-500"> · {summary.center}</span>}
          </p>
          {task.note && <p className="mt-1 line-clamp-2 text-sm text-gray-600">« {task.note} »</p>}
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-xs text-gray-400">par {author}</span>
            <button
              type="button"
              onClick={() => {
                onDismiss();
                navigate(`/reminders/case/${task.case_id}`);
              }}
              className="rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1"
            >
              Ouvrir
            </button>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fermer la notification"
        className="absolute right-2 top-2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div
        className="task-toast-timer absolute bottom-0 left-0 h-0.5 w-full bg-orange-400"
        style={{ animationDuration: `${NEW_TASK_TOAST_DURATION_MS}ms` }}
        aria-hidden="true"
      />
    </div>
  );
}
